import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { uploadsAPI } from "../services/api";
import { FILE_BASE_URL } from "../config";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "../styles/UploadLecture.css"; // Reuse existing styles
import { FaFilePdf, FaFileVideo, FaCloudUploadAlt } from "react-icons/fa";

const EditLesson = () => {
  const { courseId, lessonId } = useParams();
  const navigate = useNavigate();

  // State
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState(""); // Summary
  const [content, setContent] = useState(""); // Full Text Content
  const [youtubeUrl, setYoutubeUrl] = useState(""); // YouTube Link
  const [currentFilePath, setCurrentFilePath] = useState(null);
  const [newFile, setNewFile] = useState(null);

  // UI State
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchLesson = async () => {
      try {
        const response = await uploadsAPI.getLecture(lessonId);
        const data = response.data;

        setTitle(data.title || "");
        setDescription(data.description || "");
        setContent(data.content || "");
        setYoutubeUrl(data.youtube_url || "");
        setCurrentFilePath(data.file_path);
        setLoading(false);
      } catch (error) {
        console.error("❌ خطأ أثناء جلب بيانات المحاضرة:", error);
        setError("❌ حدث خطأ أثناء جلب بيانات المحاضرة.");
        setLoading(false);
      }
    };

    fetchLesson();
  }, [lessonId]);

  const handleUpdate = async () => {
    if (!title) {
      alert('❌ عنوان المحاضرة مطلوب.');
      return;
    }

    setUpdating(true);
    const formData = new FormData();
    formData.append("title", title);
    formData.append("description", description);
    formData.append("content", content);
    if (youtubeUrl) formData.append("youtubeUrl", youtubeUrl);
    if (newFile) {
      formData.append("file", newFile);
    }

    try {
      await uploadsAPI.updateLecture(lessonId, formData);
      alert("✅ تم تعديل المحاضرة بنجاح.");
      navigate(`/courses/${courseId}`);
    } catch (error) {
      console.error("❌ فشل في تعديل المحاضرة:", error);
      alert("❌ حدث خطأ أثناء تعديل المحاضرة. حاول مرة أخرى.");
    } finally {
      setUpdating(false);
    }
  };

  if (loading) return <p className="loading-message">⏳ جاري تحميل المحاضرة...</p>;
  if (error) return <p className="error-message">🚫 {error}</p>;

  return (
    <div className="upload-page">
      <div className="upload-container">
        <div className="upload-header">
          <h2>✏️ تعديل المحاضرة</h2>
          <p>تعديل محتوى وتفاصيل المحاضرة الحالية</p>
        </div>

        <div className="form-section">
          {/* Title */}
          <div className="form-group">
            <label>عنوان المحاضرة</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="form-control"
              placeholder="عنوان المحاضرة"
            />
          </div>

          {/* Short Description */}
          <div className="form-group">
            <label>وصف مختصر</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="form-control"
              placeholder="وصف مختصر يظهر في القائمة..."
              rows="3"
            />
          </div>

          {/* Full Text Content (Quill) */}
          <div className="form-group">
            <label>محتوى نصي (اختياري)</label>
            <div className="quill-wrapper">
              <ReactQuill
                value={content}
                onChange={setContent}
                theme="snow"
                style={{ height: '200px', marginBottom: '50px' }}
              />
            </div>
          </div>

          <div className="form-group">
            <label>رابط فيديو يوتيوب (اختياري)</label>
            <input
              type="text"
              value={youtubeUrl}
              onChange={(e) => setYoutubeUrl(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=..."
              className="form-control"
              style={{ direction: 'ltr', textAlign: 'left' }}
            />
          </div>

          {/* File Upload Section */}
          <div className="form-group file-upload-group">
            <label>ملف المحاضرة (PDF / فيديو)</label>

            {/* Current File Display */}
            {currentFilePath && !newFile && (
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                marginBottom: "15px",
                background: "#f8fafc",
                padding: "10px",
                borderRadius: "8px",
                border: "1px solid #e2e8f0"
              }}>
                {currentFilePath.endsWith('.pdf') ? <FaFilePdf color="#ef4444" size={20} /> : <FaFileVideo color="#3b82f6" size={20} />}
                <span style={{ fontSize: "14px", color: "#64748b" }}>
                  الملف الحالي: <a href={`${FILE_BASE_URL}${currentFilePath}`} target="_blank" rel="noreferrer" style={{ color: "#2563eb", textDecoration: "underline" }}>عرض الملف</a>
                </span>
              </div>
            )}

            {/* Drop Zone */}
            <div className="file-drop-zone" onClick={() => document.getElementById('file-upload').click()}>
              <input
                id="file-upload"
                type="file"
                accept=".pdf,.mp4,.doc,.docx"
                onChange={(e) => setNewFile(e.target.files[0])}
                style={{ display: 'none' }}
              />
              <div className="drop-zone-content">
                <span className="upload-icon">
                  {newFile ? (newFile.name.endsWith('.pdf') ? <FaFilePdf /> : <FaFileVideo />) : <FaCloudUploadAlt />}
                </span>
                <p>{newFile ? newFile.name : (currentFilePath ? "اضغط لاستبدال الملف الحالي" : "اضغط لرفع ملف جديد")}</p>
                {newFile && <p style={{ fontSize: "12px", color: "#e67e22", marginTop: "5px" }}>⚠️ سيتم استبدال الملف القديم</p>}
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <button
            onClick={handleUpdate}
            disabled={updating}
            className="btn-primary full-width"
            style={{ marginTop: "20px" }}
          >
            {updating ? '⏳ جاري الحفظ...' : '💾 حفظ التعديلات'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditLesson;
